import React, { useMemo } from 'react';

import { Box } from 'ui/box';

import Button from 'components/partials/button/button';
import { Loader } from 'ui/loader';
import Modal from 'components/partials/modal/modal';
import TableComponent from 'components/partials/table-component/table-component';

import { buildCoaTableRows } from '../helpers/build-coa-rows';
import { getCoaTableSettings } from './coa-table-settings';

import type { AidModelCoaDto } from 'api/generated/schemas';

interface CoaModalProps {
  coaGroups: AidModelCoaDto[];
  isLoading: boolean;
  onClose: () => void;
  previousYearLabel?: string;
  currentYearLabel?: string;
}

const CoaModal = ({
  coaGroups,
  isLoading,
  onClose,
  previousYearLabel = 'Previous Year',
  currentYearLabel = 'Current Year',
}: CoaModalProps): React.JSX.Element => {
  const rows = buildCoaTableRows(coaGroups);

  const columns = useMemo(
    () => getCoaTableSettings({ previousYearLabel, currentYearLabel }),
    [previousYearLabel, currentYearLabel]
  );

  return (
    <Modal
      headerText="Cost of Attendance"
      onCloseCallback={onClose}
      showCloseButton
      primaryAction={{ onClick: onClose }}
      modalClassName="tablet:w-[640px] p-5"
      footer={
        <Button
          type="button"
          variant="brand-filled"
          onClick={onClose}
          data-testid="results-coa-done-button"
        >
          Done
        </Button>
      }
      data-testid="results-coa-modal"
    >
      <Box
        sx={{
          position: 'relative',
          mt: 2,
          width: '100%',
          overflowX: 'auto',
          minHeight: '131px',
        }}
      >
        <Loader loading={isLoading} fullScreen={false} />

        {!isLoading && coaGroups.length === 0 && (
          <p className="py-6 text-center text-gray-600">
            No cost of attendance data available for this model.
          </p>
        )}

        {!isLoading && coaGroups.length > 0 && (
          <TableComponent
            columns={columns}
            data={rows}
            isLoading={false}
            pagination={false}
            customHeight="max-h-[50vh]"
            ariaLabel="Cost of attendance summary"
          />
        )}
      </Box>
    </Modal>
  );
};

export default CoaModal;
